import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { AgentClientProvider, ClientPicker } from "./AgentContext";
import {
  ChartLineUp, SquaresFour, ListChecks, FolderOpen, CalendarCheck, BookOpenText, ChatCircle,
  VideoCamera, Ticket, UsersThree, ShieldCheck, SignOut, List
} from "@phosphor-icons/react";

const nav = [
  { to: "/agent", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/agent/jobs", label: "Project Tracker", icon: ListChecks },
  { to: "/agent/bookkeeping", label: "Bookkeeping", icon: BookOpenText },
  { to: "/agent/documents", label: "Documents", icon: FolderOpen },
  { to: "/agent/compliance", label: "Compliance", icon: CalendarCheck },
  { to: "/agent/messages", label: "Messages", icon: ChatCircle },
  { to: "/agent/meetings", label: "Meetings", icon: VideoCamera },
  { to: "/agent/tasks", label: "Admin Tasks", icon: Ticket },
  { to: "/agent/team", label: "Team Directory", icon: UsersThree },
  { to: "/agent/settings", label: "Security 2FA", icon: ShieldCheck },
];

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };
  const [open, setOpen] = useState(false);

  return (
    <AgentClientProvider>
      <div className="min-h-screen flex overflow-x-hidden">
        {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}
        <aside className={`w-64 shrink-0 bg-[#111111] text-white flex-col fixed h-screen z-40 ${open ? "flex" : "hidden md:flex"}`}>
          <div className="p-5 flex items-center gap-2 border-b border-white/10">
            <ChartLineUp size={24} weight="fill" color="#fff" />
            <span className="font-head font-black tracking-tight">VIRTUALEENA</span>
          </div>
          <div className="px-5 pt-3 text-xs uppercase tracking-[0.2em] text-white/40">Agent Portal</div>
          <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`gnav-${n.label.replace(/[\s&]+/g, "-").toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-[#002FA7] text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                <n.icon size={18} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-white/40 text-xs mb-3">Assigned Professional</div>
            <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-white/60 hover:text-[#FF3B30] transition-colors"><SignOut size={18} /> Sign out</button>
          </div>
        </aside>
        <div className="flex-1 md:ml-64 min-w-0">
          <header className="h-16 border-b border-black/10 bg-white/70 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 md:px-8 gap-3">
            <button data-testid="agent-menu-btn" onClick={() => setOpen(true)} className="md:hidden p-1"><List size={22} /></button>
            <p className="label hidden md:block">Virtualeena Team · Agent Portal</p>
            <div className="flex items-center gap-4 ml-auto">
              <ClientPicker />
              <NotificationBell tone="dark" />
              <div className="hidden sm:flex w-9 h-9 rounded-sm bg-[#002FA7] text-white items-center justify-center font-head font-bold">{user?.name?.[0] || "A"}</div>
            </div>
          </header>
          <main className="p-4 md:p-8 max-w-[1400px]"><Outlet /></main>
        </div>
      </div>
    </AgentClientProvider>
  );
}
